import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { EmailPendingItem, LayoutMapping, Settings } from '../../lib/settings-types.js';
import { MappingEditor, mappingProblems } from '../../components/MappingEditor.js';
import {
  Button,
  ButtonLink,
  Card,
  EmptyState,
  Field,
  Select,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { ADMIN_NOTE, errorMessage, fmtWhen, useIsAdmin, useSettingsPost } from './common.js';
import {
  hiddenColumnsNote,
  visibleColumns,
  type HideableColumn,
} from '../../lib/visibleColumns.js';
import { inboxRows, viaLabel } from '../../lib/inboxes.js';
import { jumpTo } from '../../lib/jump.js';
import { CountryNamesCard } from './CountryNamesCard.js';

/** The email import log's columns under the all-zero rule (R33): a mailbox
 * that never imported has Rows at 0 or "—" on every line, so the column goes
 * and the note says so. Identity and time columns stay. */
const EMAIL_LOG_COLUMNS: HideableColumn<Settings['emailLog'][number]>[] = [
  { key: 'Attachment', get: (l) => l.filename, fixed: true },
  // Text columns are identity, not data: the rule is about numeric columns.
  { key: 'From', get: (l) => l.sender, fixed: true },
  { key: 'Status', get: (l) => l.status, fixed: true },
  { key: 'Rows', get: (l) => l.rows },
  { key: 'When', get: (l) => l.at, fixed: true },
];

type Confirm = ReturnType<typeof useConfirm>['confirm'];

/** A section's own result line, so a message never appears three sections away
 *  from the button that produced it — and so failure doesn't look like success,
 *  which it did when both went through one grey `msg` at the top of the pane. */
function Note({ note }: { note: { tone: 'ok' | 'error'; text: string } | null }) {
  if (!note) return null;
  return (
    <p className={note.tone === 'error' ? 'ui-field__error' : 'ui-field__hint'} role="status">
      {note.text}
    </p>
  );
}

type NoteState = { tone: 'ok' | 'error'; text: string } | null;

/**
 * Email & Sheets: connected Google accounts (Sheets/Gmail), IMAP app-password
 * inboxes, held email reports awaiting confirmation, the email import log, and
 * the pending-source queue.
 */
export function IngestPane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const emailLogCols = visibleColumns(settings.emailLog, EMAIL_LOG_COLUMNS);
  // One inbox per address, however it was connected (lib/inboxes.ts).
  const inboxes = inboxRows(settings.googleIdentities, settings.imapAccounts);
  const { confirm, dialog } = useConfirm();

  const [imapEmail, setImapEmail] = useState('');
  const [imapPw, setImapPw] = useState('');
  const [scanNote, setScanNote] = useState<NoteState>(null);
  const [pendingNote, setPendingNote] = useState<NoteState>(null);

  // The age limit on imported stats (server: ingest/age.ts). Saved as a whole
  // number of days; the server sweeps the held reports at once and says how
  // many it dropped.
  const [ageDays, setAgeDays] = useState(String(settings.statsMaxAgeDays));
  const [ageNote, setAgeNote] = useState<NoteState>(null);
  const ageTyped =
    /^\d{1,4}$/.test(ageDays.trim()) && Number(ageDays) >= 1 && Number(ageDays) <= 3650
      ? Number(ageDays)
      : null;
  const setAge = useMutation({
    mutationFn: (days: number) =>
      api.post<{ ok: boolean; days: number; expired: number }>('/api/settings/stats-max-age', {
        days,
      }),
    onSuccess: (r) => {
      setAgeDays(String(r.days));
      setAgeNote({
        tone: 'ok',
        text:
          `Saved — stats older than ${r.days} days are not imported` +
          (r.expired
            ? `; ${r.expired} held email report${r.expired === 1 ? '' : 's'} dropped.`
            : '.'),
      });
    },
    onError: (e) => setAgeNote({ tone: 'error', text: errorMessage(e) }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  // /configuration/ingest#email-pending — the header's "email reports waiting"
  // item lands on the card, not at the top of the pane; with nothing waiting,
  // on a card that says so. pushState navigation re-dispatches hashchange
  // (App.tsx), so a click from this very pane jumps too. The jump is instant
  // and synchronous (lib/jump.ts) and repeats at the window's load event:
  // the browser's own scroll-to-fragment at load end lands at the top when
  // the card was not rendered yet.
  const [wantPending, setWantPending] = useState(() => window.location.hash === '#email-pending');
  useEffect(() => {
    const jump = () => {
      const want = window.location.hash === '#email-pending';
      setWantPending(want);
      if (want) jumpTo(document.getElementById('email-pending'));
    };
    jump();
    window.addEventListener('hashchange', jump);
    window.addEventListener('load', jump);
    return () => {
      window.removeEventListener('hashchange', jump);
      window.removeEventListener('load', jump);
    };
  }, [settings.emailPending.length, wantPending]);

  const addImap = useSettingsPost<{ email: string; appPassword: string }>(
    '/api/settings/imap-account',
  );
  const removeImap = useSettingsPost<{ id: number }>('/api/settings/imap-account/remove');
  const assignImap = useSettingsPost<{ id: number; userId: number }>(
    '/api/settings/imap-account/assign',
  );
  const removeIdentity = useSettingsPost<{ action: 'remove'; email: string }>(
    '/api/settings/google-identity',
  );
  const removePending = useSettingsPost<{ id: number }>('/api/pending-sources/remove');

  const emailCheck = useMutation({
    mutationFn: (opts?: { identity?: string; deep?: boolean }) =>
      api.post('/api/email-check', {
        ...(opts?.identity ? { identity: opts.identity } : {}),
        ...(opts?.deep ? { deep: true } : {}),
      }),
    onSuccess: (_r, opts) =>
      setScanNote({
        tone: 'ok',
        text: opts?.deep
          ? 'Deep scan queued — digging through up to 500 messages; results land in the log below.'
          : 'Scan queued — results appear in the log below in a minute or two.',
      }),
    onError: (e) => setScanNote({ tone: 'error', text: errorMessage(e) }),
  });

  const checkPending = useMutation({
    mutationFn: () => api.post('/api/pending-sources/check'),
    onSuccess: () =>
      setPendingNote({ tone: 'ok', text: 'Checked — anything with data now waits in Add stats.' }),
    onError: (e) => setPendingNote({ tone: 'error', text: errorMessage(e) }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  return (
    <div>
      <Card
        title="Connected Google accounts"
        description={
          <>
            Used to open private Sheets shared with that account, and (with the Gmail permission) to
            scan the inbox for report attachments. Connecting requires the Google client on the APIs
            pane; the OAuth redirect URI is{' '}
            <span className="mono">{settings.googleRedirectUri}</span>.
          </>
        }
        actions={
          <>
            <ButtonLink size="sm" href="/api/google/connect?scopes=sheets">
              Connect for Sheets
            </ButtonLink>
            <ButtonLink size="sm" href="/api/google/connect?scopes=sheets,gmail">
              Connect for Sheets + Gmail
            </ButtonLink>
          </>
        }
      >
        {settings.googleIdentities.length ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Account</th>
                <th className="l">User</th>
                <th className="l">Inbox scanned</th>
                <th>Last import</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {settings.googleIdentities.map((g) => (
                <tr key={g.email}>
                  <td className="l">{g.email}</td>
                  <td className="l">{g.user ?? '—'}</td>
                  <td className="l">{g.gmail ? 'yes' : 'no'}</td>
                  <td>{fmtWhen(g.lastImport)}</td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm<true>({
                            title: `Disconnect ${g.email}?`,
                            body: (
                              <p>
                                Sheets shared privately with this account stop being readable, and
                                its inbox stops being scanned. Reconnecting means going through
                                Google sign-in again.
                              </p>
                            ),
                            choices: [{ label: 'Disconnect', value: true, variant: 'danger' }],
                          });
                          if (ok) removeIdentity.mutate({ action: 'remove', email: g.email });
                        })();
                      }}
                    >
                      Disconnect
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No Google account connected yet.</EmptyState>
        )}
      </Card>

      <Card
        title="Inboxes scanned for reports"
        description={
          isAdmin
            ? 'Every inbox the scan reads for report attachments, however it was connected. A Google account connected above with the Gmail permission is read through Google’s own API and needs nothing else. An app password is the other way in, for a mailbox you would rather not sign in with: a 16-character code Google issues for one app, at myaccount.google.com → Security → App passwords, stored encrypted and never shown again. An address connected both ways is read twice — keep one.'
            : ADMIN_NOTE
        }
      >
        {isAdmin ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addImap.mutate(
                { email: imapEmail, appPassword: imapPw },
                {
                  onSuccess: () => {
                    setImapEmail('');
                    setImapPw('');
                  },
                },
              );
            }}
          >
            <div className="cfg-grid">
              <Field label="Inbox address">
                {(aria) => (
                  <TextInput
                    {...aria}
                    type="email"
                    autoComplete="username"
                    placeholder="inbox@gmail.com"
                    value={imapEmail}
                    onChange={(e) => setImapEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label="App password" hint="Four groups of four characters.">
                {(aria) => (
                  <TextInput
                    {...aria}
                    type="password"
                    reveal
                    autoComplete="off"
                    value={imapPw}
                    onChange={(e) => setImapPw(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <Toolbar className="cfg-actions">
              <Button type="submit" loading={addImap.isPending} disabled={!imapEmail || !imapPw}>
                Add inbox
              </Button>
              {addImap.isError ? (
                <span className="ui-field__error">{errorMessage(addImap.error)}</span>
              ) : null}
            </Toolbar>
          </form>
        ) : null}

        {inboxes.length ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Inbox</th>
                <th className="l">Connected via</th>
                <th className="l">User</th>
                <th>Last import</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {inboxes.map((m) => (
                <tr key={m.email}>
                  <td className="l">{m.email}</td>
                  <td className="l">
                    {viaLabel(m.via)}
                    {m.password && !m.password.enabled ? ' (paused)' : ''}
                    {m.via.length > 1 ? (
                      <>
                        {' '}
                        <span
                          className="pill warn"
                          title="Connected through its Google sign-in and an app password: the scan reads it twice. Remove the app password, or disconnect the Google account above."
                        >
                          read twice
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td className="l">
                    {m.password && isAdmin ? (
                      // Always a picker, so an assignment can be corrected. There is
                      // no "nobody" option because the route requires a user id.
                      <Select
                        size="sm"
                        aria-label={`User for ${m.email}`}
                        value={
                          settings.users.find((u) => u.username === m.password?.user)?.id ?? ''
                        }
                        onChange={(e) =>
                          e.target.value &&
                          assignImap.mutate({ id: m.password!.id, userId: Number(e.target.value) })
                        }
                      >
                        <option value="">assign user…</option>
                        {settings.users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.username}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      m.users.join(', ') || '—'
                    )}
                  </td>
                  <td>{fmtWhen(m.lastImport)}</td>
                  <td>
                    <Toolbar>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => emailCheck.mutate({ identity: m.email })}
                      >
                        Check now
                      </Button>
                      {isAdmin && m.password ? (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm<true>({
                                title: `Remove the app password for ${m.email}?`,
                                body: (
                                  <p>
                                    {m.via.includes('google')
                                      ? 'Its app password is deleted; the inbox is still scanned through its Google sign-in above.'
                                      : 'Its app password is deleted and the inbox stops being scanned.'}{' '}
                                    Already-imported rows are kept.
                                  </p>
                                ),
                                choices: [{ label: 'Remove', value: true, variant: 'danger' }],
                              });
                              if (ok) removeImap.mutate({ id: m.password!.id });
                            })();
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Toolbar>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>
            No inbox connected yet — reports arriving by email won’t be picked up. Connect a Google
            account with the Gmail permission above, or add an app password.
          </EmptyState>
        )}

        <Toolbar className="cfg-actions">
          <Button onClick={() => emailCheck.mutate(undefined)} loading={emailCheck.isPending}>
            Check all inboxes now
          </Button>
          {isAdmin ? (
            <Button
              variant="ghost"
              onClick={() => emailCheck.mutate({ deep: true })}
              disabled={emailCheck.isPending}
              title="Reads up to 500 messages per inbox, any read-state — for finding reports the routine scan missed. The age limit still applies: stats older than it are skipped."
            >
              Deep scan
            </Button>
          ) : null}
        </Toolbar>
        <Note note={scanNote} />
      </Card>

      <Card
        title="Age limit"
        description="Stats older than this are never imported — from email reports, uploads, sheets or provider pulls alike. A held email report whose rows are all older is dropped at the next inbox check instead of waiting for someone to discard it."
      >
        <Field
          label="Ignore stats older than"
          hint={
            isAdmin
              ? 'days before today; 90 (three months) unless changed'
              : 'days before today — an admin changes this'
          }
        >
          {(aria) => (
            <TextInput
              {...aria}
              type="number"
              min={1}
              max={3650}
              step="1"
              inputMode="numeric"
              value={ageDays}
              onChange={(e) => setAgeDays(e.target.value)}
              disabled={!isAdmin}
            />
          )}
        </Field>
        {isAdmin ? (
          <Toolbar className="cfg-actions">
            <Button
              size="sm"
              loading={setAge.isPending}
              disabled={ageTyped == null || ageTyped === settings.statsMaxAgeDays}
              onClick={() => {
                if (ageTyped != null) setAge.mutate(ageTyped);
              }}
            >
              Save age limit
            </Button>
          </Toolbar>
        ) : null}
        <Note note={ageNote} />
      </Card>

      <CountryNamesCard isAdmin={isAdmin} />

      {settings.emailPending.length ? (
        <Card
          id="email-pending"
          title="Email reports awaiting confirmation"
          description="Attachments that look like stats reports. Nothing is imported until the column matching below is confirmed."
        >
          {settings.emailPending.map((p) => (
            <EmailPendingCard key={p.id} item={p} confirm={confirm} />
          ))}
        </Card>
      ) : wantPending ? (
        <Card id="email-pending" title="Email reports awaiting confirmation">
          <EmptyState>
            No email reports are waiting. A report whose stats are all older than the age limit is
            skipped, or dropped by the inbox check if it was already held — the import log below
            says which.
          </EmptyState>
        </Card>
      ) : null}

      <Card title="Email import log">
        {settings.emailLog.length ? (
          <>
            <table className="stat-table">
              <thead>
                <tr>
                  {emailLogCols.shown.map((c) => (
                    <th
                      key={c.key}
                      className={c.key === 'Rows' || c.key === 'When' ? undefined : 'l'}
                    >
                      {c.key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {settings.emailLog.map((l, i) => (
                  <tr key={`${l.templateKey}-${i}`}>
                    {emailLogCols.shown.map((c) => {
                      switch (c.key) {
                        case 'Attachment':
                          return (
                            <td key={c.key} className="l mono">
                              {l.filename}
                            </td>
                          );
                        case 'From':
                          return (
                            <td key={c.key} className="l">
                              {l.sender ?? '—'}
                            </td>
                          );
                        case 'Status':
                          return (
                            <td key={c.key} className="l">
                              {l.status}
                              {l.error ? <div className="form-err">{l.error}</div> : null}
                            </td>
                          );
                        case 'Rows':
                          return <td key={c.key}>{l.rows ?? '—'}</td>;
                        case 'When':
                          return <td key={c.key}>{fmtWhen(l.at)}</td>;
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {emailLogCols.hidden.length ? (
              <p className="table-note">{hiddenColumnsNote(emailLogCols.hidden)}</p>
            ) : null}
          </>
        ) : (
          <EmptyState>Nothing imported from email yet.</EmptyState>
        )}
      </Card>

      <Card
        title="Pending sources"
        description="URLs that answered but had no rows when added. Retried daily; when data appears the proposed column matching waits here for confirmation (confirm from Add stats by pasting the URL again)."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => checkPending.mutate()}
            loading={checkPending.isPending}
          >
            Check now
          </Button>
        }
      >
        {settings.pendingSources.length ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">URL</th>
                <th className="l">Status</th>
                <th>Last checked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {settings.pendingSources.map((p) => (
                <tr key={p.id}>
                  <td className="l mono">{p.url}</td>
                  <td className="l">
                    {p.status}
                    {p.lastError ? (
                      <div className={p.status === 'waiting' ? 'ui-field__hint' : 'form-err'}>
                        {p.lastError}
                      </div>
                    ) : null}
                  </td>
                  <td>{fmtWhen(p.lastChecked)}</td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm<true>({
                            title: 'Remove this pending source?',
                            body: (
                              <p>It stops being retried. You can add it again from Add stats.</p>
                            ),
                            choices: [{ label: 'Remove', value: true, variant: 'danger' }],
                          });
                          if (ok) removePending.mutate({ id: p.id });
                        })();
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>Queue is empty.</EmptyState>
        )}
        <Note note={pendingNote} />
      </Card>

      {dialog}
    </div>
  );
}

function EmailPendingCard({ item, confirm }: { item: EmailPendingItem; confirm: Confirm }) {
  const qc = useQueryClient();
  const [mapping, setMapping] = useState<LayoutMapping>(
    item.mapping ?? { format: 'csv', fields: {}, date_format: 'iso' },
  );
  const [err, setErr] = useState<string | null>(null);
  const confirmImport = useMutation({
    mutationFn: () => api.post('/api/email-pending/confirm', { id: item.id, mapping }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
    onError: (e) => setErr(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.post('/api/email-pending/remove', { id: item.id }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  return (
    <div className="cfg-subcard">
      <p className="table-note">
        <b>{item.filename}</b> from {item.sender ?? 'unknown'}
        {item.subject ? ` — “${item.subject}”` : ''}
        {item.providerGuess ? ` · looks like ${item.providerGuess}` : ''}
        {item.feedIdsSeen?.length ? ` · feeds seen: ${item.feedIdsSeen.join(', ')}` : ''}
      </p>
      <MappingEditor
        columns={item.columns ?? []}
        samples={item.samples}
        mapping={mapping}
        onChange={setMapping}
      />
      {err ? <p className="ui-field__error">{err}</p> : null}
      <Toolbar className="cfg-actions">
        <Button
          onClick={() => confirmImport.mutate()}
          loading={confirmImport.isPending}
          disabled={mappingProblems(mapping).length > 0}
        >
          Confirm &amp; import
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            void (async () => {
              const ok = await confirm<true>({
                title: 'Discard this report?',
                body: (
                  <p>
                    <b>{item.filename}</b> is dropped and this report type is remembered as
                    not-a-report, so future copies are ignored. You can undo that under Providers →
                    Ignored email report types.
                  </p>
                ),
                choices: [{ label: 'Discard', value: true, variant: 'danger' }],
              });
              if (ok) remove.mutate();
            })();
          }}
          loading={remove.isPending}
        >
          Not a report — discard
        </Button>
      </Toolbar>
    </div>
  );
}
